import type { PhaseRun, TenantDetail } from '../lib/api';

/** Provisioning progress visual.
 *
 * Two modes:
 * - **Detailed** (admin): 19 segments, one per phase, colored by phase
 *   status, with phase-name tooltips and start/end labels. Useful for
 *   debugging and watching the orchestrator.
 * - **Simple** (operator): one continuous filled bar with the percentage.
 *   No phase names, no tooltips, no segments — operators see overall
 *   progress without learning the internal orchestration steps.
 */
export default function PipelineProgressBar({
  tenant, simple = false,
}: { tenant: TenantDetail; simple?: boolean }) {
  const phases = tenant.phases || [];
  const failed = phases.find(p => p.status === 'failed');
  const running = phases.find(p => p.status === 'running');

  // Compute pct from multiple sources, in priority order. The API
  // returns `phases: []` for non-admin callers (operators) by design —
  // exposing the 19 internal step count leaks the orchestrator surface.
  // For operators we fall back to mailbox_counts; for done tenants we
  // always clamp to 100% regardless of phase rows (those get reset
  // when an admin re-runs a single phase).
  const mc: any = tenant.mailbox_counts || {};
  const connected = Number(mc.connected ?? mc.oauth_done ?? 0);
  const errors    = Number(mc.errors    ?? mc.oauth_error ?? 0);
  const mbTotal   = Number(mc.total ?? 0);
  let pct: number;
  if (tenant.status === 'done') {
    pct = 100;
  } else if (phases.length > 0) {
    // Admin view — phase-derived.
    const phaseDone = phases.filter(p => p.status === 'done').length;
    pct = Math.round((phaseDone / phases.length) * 100);
  } else if (mbTotal > 0) {
    // Operator view — mailbox-derived. Connected + errors count as
    // "completed" so a tenant with partial OAuth failures still
    // shows the right fill rather than stalling at 0.
    pct = Math.round(((connected + errors) / mbTotal) * 100);
  } else if (tenant.status === 'failed' || tenant.status === 'warning') {
    // No phase data and no mailboxes yet, but the run errored — show
    // a small fill so the bar communicates "not nothing."
    pct = 10;
  } else {
    pct = 0;
  }
  // Clamp to [0, 100] defensively.
  pct = Math.max(0, Math.min(100, pct));

  if (simple) {
    const headline = failed || tenant.status === 'failed'
      ? 'Provisioning paused — needs attention'
      : tenant.status === 'done'
        ? 'Setup complete'
        : tenant.status === 'warning'
          ? `Setup mostly done — ${errors} mailbox(es) need attention`
          : running || tenant.status === 'running' || tenant.status === 'queued' || tenant.status === 'rerun_queued'
            ? 'Setting up your inboxes…'
            : 'Setup queued';
    const fillColor = failed || tenant.status === 'failed' ? 'bg-err'
      : tenant.status === 'done' ? 'bg-ok'
      : tenant.status === 'warning' ? 'bg-warn'
      : 'bg-accent';
    const isAnimating = tenant.status === 'running' || tenant.status === 'rerun_queued';
    return (
      <div className="card p-5">
        <div className="flex items-baseline justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-wider text-dim mb-1">Provisioning</div>
            <div className="text-sm font-medium text-ink">{headline}</div>
          </div>
          <div className="text-2xl tabular-nums font-semibold text-ink">{pct}%</div>
        </div>
        <div className="h-2 rounded-full overflow-hidden bg-white/[0.04]">
          <div
            className={`h-full ${fillColor} transition-all duration-500 ${isAnimating ? 'animate-pulse' : ''}`}
            style={{ width: `${pct}%` }}
          />
        </div>
        {mbTotal > 0 && (
          <div className="mt-2 flex items-center justify-between text-[11px] text-dim tabular-nums">
            <span>{connected} of {mbTotal} mailbox(es) connected</span>
            {errors > 0 && <span className="text-warn">{errors} need attention</span>}
          </div>
        )}
      </div>
    );
  }

  const phaseDone = phases.filter(p => p.status === 'done').length;
  const headline = failed
    ? `Failed at ${prettyPhase(failed.phase)}`
    : running
      ? `Running ${prettyPhase(running.phase)}`
      : tenant.status === 'done'
        ? 'All phases complete'
        : `${phaseDone} of ${phases.length} phases complete`;

  return (
    <div className="card p-5">
      <div className="flex items-baseline justify-between mb-3">
        <div>
          <div className="text-xs uppercase tracking-wider text-dim mb-1">Provisioning</div>
          <div className="text-sm font-medium text-ink">{headline}</div>
        </div>
        <div className="text-2xl tabular-nums font-semibold text-ink">{pct}%</div>
      </div>
      <div className="flex gap-[2px] h-2 rounded-full overflow-hidden bg-white/[0.04]">
        {phases.map(p => (
          <div
            key={p.phase}
            title={`${prettyPhase(p.phase)} · ${p.status}${p.error ? ` — ${p.error}` : ''}`}
            className={`flex-1 transition ${segClass(p)}`}
          />
        ))}
      </div>
      <div className="mt-2 flex items-center justify-between text-[11px] text-dim">
        <span>{prettyPhase(phases[0]?.phase || '')}</span>
        <span>{prettyPhase(phases[phases.length - 1]?.phase || '')}</span>
      </div>
    </div>
  );
}

function segClass(p: PhaseRun): string {
  switch (p.status) {
    case 'done':    return 'bg-ok/80';
    case 'running': return 'bg-run animate-pulse';
    case 'failed':  return 'bg-err';
    case 'skipped': return 'bg-dim/30';
    default:        return 'bg-white/[0.06]';
  }
}

function prettyPhase(phase: string): string {
  if (!phase) return '';
  return phase
    .replace(/_/g, ' ')
    .replace(/\bupn\b/i, 'tenant login')
    .replace(/\bdkim\b/i, 'DKIM')
    .replace(/\bdns\b/i, 'DNS')
    .replace(/\bsmtp\b/i, 'SMTP')
    .replace(/\boauth\b/i, 'OAuth')
    .replace(/\btxt\b/i, 'TXT')
    .replace(/\bga\b/i, 'GA')
    .replace(/\btotp\b/i, 'TOTP')
    .replace(/\bsd\b/i, 'security defaults')
    .replace(/\bex\b/i, 'Exchange')
    .replace(/\bmb\b/i, 'mailbox')
    .replace(/^\w/, c => c.toUpperCase());
}
